import React from "react";
import { motion } from "framer-motion";
import { FiCheckCircle, FiUsers, FiMapPin, FiAward, FiStar, FiCompass, FiGlobe, FiHeart, FiShield } from "react-icons/fi";

const AboutUs = () => {
  return (
    <div className="w-full overflow-x-hidden text-gray-800 bg-white">

      {/* ================= HERO ================= */}
      <section className="relative h-[85vh] w-full overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
          alt="Travel Hero"
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        <div className="relative z-20 max-w-6xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Discover the World With <span className="text-blue-400">Celestial Tours</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-10 font-light leading-relaxed">
              Crafting extraordinary journeys with precision, passion, and personalized service for every traveler.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-600 transition-colors"
              >
                Explore Our Tours
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Contact Us
              </motion.button>
            </div>
          </motion.div>
        </div>
        
        <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
      </section>

      {/* ================= ABOUT COMPANY ================= */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-blue-100 rounded-full opacity-70"></div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-100 rounded-full opacity-70"></div>
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="Our Team"
              className="rounded-2xl shadow-xl relative z-10 w-full h-[500px] object-cover"
            />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-800">Our Story</h2>
            <div className="w-20 h-1 bg-blue-500 mb-8"></div>
            <p className="text-gray-600 leading-relaxed mb-6 text-lg">
              Founded in 2010, Celestial Tours began with a simple mission: to transform ordinary trips into extraordinary experiences. What started as a small team of travel enthusiasts has grown into a premier travel service provider with a global footprint.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8 text-lg">
              Today, we specialize in crafting bespoke journeys—from romantic honeymoons and family vacations to adventurous expeditions and corporate retreats. With over a decade of expertise.
            </p>
            
            <div className="grid grid-cols-2 gap-6 mt-10">
              <div className="flex items-start">
                <FiCheckCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">10,000+ Happy Travelers</h3>
                  <p className="text-gray-600 text-sm mt-1">Served with excellence</p>
                </div>
              </div>
              <div className="flex items-start">
                <FiCheckCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">50+ Destinations</h3>
                  <p className="text-gray-600 text-sm mt-1">Across 6 continents</p>
                </div>
              </div>
              <div className="flex items-start">
                <FiCheckCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">Award Winning</h3>
                  <p className="text-gray-600 text-sm mt-1">5 industry awards</p>
                </div>
              </div>
              <div className="flex items-start">
                <FiCheckCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">24/7 Support</h3>
                  <p className="text-gray-600 text-sm mt-1">Always here for you</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= MISSION & VISION ================= */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Mission & Vision</h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-4xl mx-auto text-lg">
              We're driven by a passion for exploration and a commitment to excellence in every journey we create.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                  <FiCompass className="text-blue-600" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                To transform travel dreams into reality by providing personalized, seamless, and unforgettable experiences that exceed expectations. We strive to make every journey meaningful, comfortable, and enriching through our attention to detail and deep understanding of diverse travel needs.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                  <FiGlobe className="text-blue-600" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                To become the world's most trusted travel partner, recognized for innovation, sustainability, and exceptional customer experiences. We envision a future where travel is accessible, transformative, and leaves a positive impact on both travelers and the destinations we visit.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE US ================= */}
      <section className="py-10 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose Celestial Tours</h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto mb-6"></div>
            
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: <FiCompass size={40} />, 
                title: "Tailored Itineraries", 
                text: "Every journey is uniquely crafted to match your preferences, budget, and travel style. We listen carefully to understand your vision and bring it to life.",
                color: "blue"
              },
              { 
                icon: <FiStar size={40} />, 
                title: "Premium Experiences", 
                text: "From luxury accommodations to exclusive local encounters, we ensure every aspect of your trip exceeds expectations with our carefully vetted partners.",
                color: "amber"
              },
              { 
                icon: <FiShield size={40} />, 
                title: "Trust & Security", 
                text: "Your safety and satisfaction are our top priorities. We provide comprehensive support, secure transactions, and reliable local contacts throughout your journey.",
                color: "green"
              },
              { 
                icon: <FiMapPin size={40} />, 
                title: "Global Expertise", 
                text: "With first-hand knowledge of destinations worldwide, our travel specialists provide insider tips and access to hidden gems you won't find in guidebooks.",
                color: "purple"
              },
              { 
                icon: <FiUsers size={40} />, 
                title: "Dedicated Support", 
                text: "From planning to return, you'll have a personal travel consultant available 24/7 to handle any needs or changes to your itinerary.",
                color: "red"
              },
              { 
                icon: <FiHeart size={40} />, 
                title: "Sustainable Travel", 
                text: "We're committed to responsible tourism that benefits local communities and preserves the natural and cultural heritage of the places we visit.",
                color: "gray"
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className={`p-4 flex flex-row items-center gap-4 rounded-xl bg-${item.color}-100 text-${item.color}-600 w-fit mb-6`}>
                  {item.icon}
                  <h3 className="font-bold text-xl text-gray-800">{item.title}</h3>
                </div>
                
                <p className="text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= OUR PROCESS ================= */}
      <section className="py-10 bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">How We Craft Your Perfect Journey</h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto"></div>
           
          </motion.div>

          <div className="grid md:-mt-6 md:grid-cols-4 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-1 bg-white/20 z-0"></div>
            
            {[
              { 
                step: "01", 
                title: "Consultation", 
                description: "We begin with an in-depth conversation to understand your travel dreams, preferences, and expectations.",
                icon: <FiUsers size={28} />
              },
              { 
                step: "02", 
                title: "Custom Planning", 
                description: "Our experts design a personalized itinerary with handpicked accommodations, activities, and experiences.",
                icon: <FiCompass size={28} />
              },
              { 
                step: "03", 
                title: "Seamless Booking", 
                description: "We handle all reservations, tickets, and logistics so you can focus on the excitement of your upcoming journey.",
                icon: <FiCheckCircle size={28} />
              },
              { 
                step: "04", 
                title: "Travel & Support", 
                description: "Enjoy your trip with 24/7 support, local contacts, and the confidence that every detail has been arranged.",
                icon: <FiStar size={28} />
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative z-10 text-center"
              >
               
                <div className="bg-white backdrop-blur-sm p-8 rounded-2xl border border-gray-800 h-full">
                  <div className="text-blue-400 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-xl mb-4">{item.title}</h3>
                  <p className="opacity-90 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">What Our Travelers Say</h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto "></div>
           
          </motion.div>

          <div className="grid md:-mt-6 md:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah Johnson",
                location: "New York, USA",
                text: "Our honeymoon to Bali was absolutely magical! Celestial Tours thought of every detail, from the private villa with ocean views to the romantic dinner. We couldn't have asked for a perfect start to our marriage.",
                rating: 5,
                image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
              },
              {
                name: "Michael Chen",
                location: "Toronto, Canada",
                text: "As a frequent business traveler, I appreciate efficiency and reliability. Celestial Tours consistently delivers both. Their attention to detail and 24/7 support gives me peace of mind no matter where I'm traveling.",
                rating: 5,
                image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
              },
              {
                name: "The Rodriguez Family",
                location: "Madrid, Spain",
                text: "Traveling with three children can be challenging, but Celestial Tours made our European vacation seamless. The kid-friendly activities, accommodations, and made it our best family trip yet!",
                rating: 5,
                image: "https://images.unsplash.com/photo-1511988617509-a57c8a288659?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
              }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FiStar key={i} className="text-amber-400 fill-amber-400" size={20} />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                    <img src={testimonial.image} alt={testimonial.name} className="object-cover w-full h-full" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">{testimonial.name}</h4>
                    <p className="text-gray-500 text-sm">{testimonial.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="py-12 bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Begin Your Journey?</h2>
            <p className="text-xl max-w-2xl mx-auto mb-10 opacity-90">
              Let us transform your travel dreams into unforgettable experiences. Contact us today to start planning your next adventure.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-600 transition-colors"
              >
                Plan Your Trip
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-transparent border-2 border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                View Our Destinations
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;